/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import { RecorderUploadStates } from "constants/RecorderUploadStates";
import { RecordingSessionProcessingStates } from "constants/RecordingSessionProcessingStates";
import { updateIfExists } from "reducers/utilities/UpdateIfExists";
import { RecordingSessionActions } from "types/Actions";
import { RecordingSessionsSet } from "types/RecordingSession";

export default function RecordingSessionsReducer(
  recordingSessions: RecordingSessionsSet,
  action: RecordingSessionActions
): RecordingSessionsSet {
  switch (action.type) {
    case ActionTypes.LOAD_PROJECT:
      return action.recordingSessions;
    case ActionTypes.INSERT_RECORDING_SESSION:
      return {
        ...recordingSessions,
        [action.recordingSession.id]: action.recordingSession,
      };
    case ActionTypes.RECORDING_STARTED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          ...session,
          recordingSessionTracks: updateIfExists(
            session.recordingSessionTracks,
            action.from,
            {
              startTime: action.recordingStartTime,
            },
            (track) => track.adobeId
          ),
        })
      );
    }
    case ActionTypes.PARTICIPANT_RECORDING_SESSION_UPLOAD_SUCCEEDED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          recordingSessionTracks: updateIfExists(
            session.recordingSessionTracks,
            action.from,
            {
              uploadState: RecorderUploadStates.COMPLETE,
            },
            (track) => track.adobeId
          ),
        })
      );
    }
    case ActionTypes.PARTICIPANT_RECORDING_SESSION_UPLOAD_FAILED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          recordingSessionTracks: updateIfExists(
            session.recordingSessionTracks,
            action.from,
            (track) => ({
              uploadState:
                track.uploadState === RecorderUploadStates.COMPLETE
                  ? RecorderUploadStates.COMPLETE
                  : RecorderUploadStates.FAILED,
            }),
            (track) => track.adobeId
          ),
        })
      );
    }
    case ActionTypes.RECORDING_SESSION_ALREADY_FINALIZED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          ...session,
          recordingSessionTracks: session.recordingSessionTracks.map(
            (track) => ({
              ...track,
              uploadState: RecorderUploadStates.COMPLETE,
            })
          ),
        })
      );
    }
    case ActionTypes.RECORDING_STOPPED: {
      if (!action.recordingSessionId) {
        return recordingSessions;
      }
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          ...session,
          recordingSessionTracks: session.recordingSessionTracks.map(
            (track) => ({
              ...track,
              uploadState: [
                RecorderUploadStates.COMPLETE,
                RecorderUploadStates.FAILED,
              ].includes(track.uploadState)
                ? track.uploadState
                : RecorderUploadStates.STARTED,
            })
          ),
        })
      );
    }
    case ActionTypes.RECORDING_SESSION_PROCESSING_STARTED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          ...session,
          processingState: RecordingSessionProcessingStates.PROCESSING,
        })
      );
    }
    case ActionTypes.RECORDING_SESSION_PROCESSING_SUCCEEDED: {
      return updateIfExists(
        recordingSessions,
        action.recordingSessionId,
        (session) => ({
          ...session,
          processingState: RecordingSessionProcessingStates.COMPLETE,
          recordingSessionTracks: action.recordingSessionTracks,
        })
      );
    }
    default:
      return recordingSessions;
  }
}
